/**
 * UX2 — Commercial Negotiation Workspace shell for Sales Quotation detail.
 */
import React, { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Dropdown,
  Row,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  MoreOutlined,
  PrinterOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import {
  EnterprisePageHeader,
  WorkflowStrip,
  WorkspaceLayout,
} from '@/components/enterprise';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { PendingApproverText } from '@/components/approval/PendingApproverText';
import { ENTITY_TYPE_QUOTATION } from '@/constants/approval-entity-types';
import type { ApprovalSummaryDTO } from '@/services/approval.service';
import { QuotationRevisionComparePanel } from '@/components/crm/QuotationRevisionComparePanel';
import { QuotationSubmissionHistoryPanel } from '@/components/crm/QuotationSubmissionHistoryPanel';
import { richTextToPrintHtml } from '@/utils/richText';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import {
  QuotationAcceptConfirmModal,
  QuotationAcceptanceBanner,
  QuotationApprovalImpactCard,
  QuotationAttentionPanel,
  QuotationCommercialHero,
  QuotationCompareDrawer,
  QuotationHistoricalBanner,
  QuotationIntelligenceRail,
  QuotationItemsChangedBadge,
  QuotationPrimaryCtaButton,
  QuotationRevisionHistoryTab,
  QuotationRevisionImpactHero,
  QuotationRevisionSelector,
  QuotationRevisionTimeline,
  QuotationSendConfirmModal,
  QuotationActivityTab,
  QuotationEstimateChangeBanner,
  QuotationEstimateSourceCard,
} from '@/components/crm/quotation-workspace';
import DocumentList from '@/components/documents/DocumentList';
import InheritedAttachmentsCard from '@/components/documents/InheritedAttachmentsCard';
import DocumentUpload from '@/components/documents/DocumentUpload';
import type { Quotation, QuotationItem } from '@/services/quotation.service';
import type { ContractReview } from '@/services/contractReview.service';
import {
  displayQuotationRevisionLabel,
  formatQuotationPrintIdentity,
} from '@/utils/quotationRevisionPolicy';
import {
  buildQuotationRevisionInsights,
  buildRevisionWorkflowCurrentIndex,
  buildRevisionWorkflowStages,
  resolveFamilyRole,
} from '@/utils/quotationRevisionIntelligence';
import { resolveQuotationPrimaryCta } from '@/utils/quotationPrimaryCta';
import { resolveQuotationOwnerLabel } from '@/utils/quotationOwnerDisplay';
import type { QuotationRevisionCompareResult } from '@/components/crm/QuotationRevisionComparePanel';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useUserDisplayResolver } from '@/hooks/useUserDisplayResolver';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';

const { Text, Title } = Typography;

function departmentLabel(
  departmentId: string | null | undefined,
  departments: Array<{ id: string; department_code: string; department_name: string }>
): string {
  if (!departmentId) return '—';
  const d = departments.find((x) => x.id === departmentId);
  return d ? `${d.department_code} - ${d.department_name}` : departmentId;
}

function formatQuotationProjectLabel(quotation: Quotation): string {
  const code = (quotation.project_number || quotation.project_summary?.project_number || '').trim();
  const name = (quotation.project_name || quotation.project_summary?.project_name || '').trim();
  if (code && name) return `${code} - ${name}`;
  return code || name || '—';
}

export type QuotationWorkspaceActions = {
  onBack: () => void;
  onPrimaryCta: (key: ReturnType<typeof resolveQuotationPrimaryCta>['key']) => void;
  onEdit?: () => void;
  onPrint: () => void;
  onTransfer: () => void;
  onRevise: () => void;
  onCompare?: () => void;
  onDelete?: () => void;
  primaryCtaLoading?: boolean;
};

type Props = {
  quotation: Quotation;
  family: Quotation[];
  familyLoading?: boolean;
  comparison?: QuotationRevisionCompareResult;
  compareLoading?: boolean;
  currentOffer?: Quotation | null;
  contractReviews?: ContractReview[];
  estimateLabel?: string | null;
  onOpenEstimate?: () => void;
  revenueServiceTypeLabel?: string;
  itemColumns: ColumnsType<QuotationItem>;
  showUniversalApproval?: boolean;
  universalApprovalPending?: boolean;
  universalApprovalApproved?: boolean;
  approvalSummary?: ApprovalSummaryDTO;
  isTradingRental?: boolean;
  canViewProfitability?: boolean;
  canEdit?: boolean;
  canRevise?: boolean;
  canDelete?: boolean;
  editDisabledReason?: string;
  reviseDisabledReason?: string;
  deleteDisabledReason?: string;
  linkedSalesOrders?: Array<Record<string, unknown>>;
  /** Prefer explicit SO number when known (e.g. from sales_order_id lookup). */
  salesOrderNumber?: string | null;
  actions: QuotationWorkspaceActions;
  onApprovalInvalidate?: () => void;
  onApproveViaDocument?: (remarks?: string) => Promise<void>;
  onRejectViaDocument?: (payload: unknown) => Promise<void>;
  sendConfirmOpen?: boolean;
  acceptConfirmOpen?: boolean;
  onSendConfirm?: () => void;
  onSendCancel?: () => void;
  onAcceptConfirm?: () => void;
  onAcceptCancel?: () => void;
  sendLoading?: boolean;
  acceptLoading?: boolean;
  priorOfferRevision?: number | null;
  overviewExtra?: React.ReactNode;
  latestApprovedEstimateId?: string | null;
  latestApprovedEstimateRevision?: number | null;
  currentEstimationRevision?: number | null;
  estimateCostDelta?: number | null;
  onOpenEstimateReconciliation?: (targetEstimationId: string) => void;
};

export const QuotationNegotiationWorkspace: React.FC<Props> = ({
  quotation,
  family,
  familyLoading,
  comparison,
  compareLoading,
  currentOffer,
  contractReviews = [],
  estimateLabel,
  onOpenEstimate,
  revenueServiceTypeLabel,
  itemColumns,
  showUniversalApproval,
  universalApprovalPending,
  universalApprovalApproved,
  approvalSummary,
  isTradingRental,
  canViewProfitability,
  canEdit,
  canRevise,
  actions,
  onApprovalInvalidate,
  onApproveViaDocument,
  onRejectViaDocument,
  sendConfirmOpen,
  acceptConfirmOpen,
  onSendConfirm,
  onSendCancel,
  onAcceptConfirm,
  onAcceptCancel,
  sendLoading,
  acceptLoading,
  priorOfferRevision,
  overviewExtra,
  latestApprovedEstimateId,
  latestApprovedEstimateRevision,
  currentEstimationRevision,
  estimateCostDelta,
  onOpenEstimateReconciliation,
  linkedSalesOrders = [],
  salesOrderNumber: salesOrderNumberProp = null,
}) => {
  const navigate = useNavigate();
  const { data: departments = [] } = useDepartmentsForSelection();
  const salesOrderNumberFromLinks =
    (linkedSalesOrders.find(
      (r) => String(r.sales_order_id || r.id) === String(quotation.sales_order_id)
    )?.sales_order_number as string | undefined) ||
    (linkedSalesOrders[0]?.sales_order_number as string | undefined) ||
    null;
  const salesOrderNumber = salesOrderNumberProp || salesOrderNumberFromLinks;
  const { resolve: resolveUserDisplay } = useUserDisplayResolver();
  const ownerLabel = resolveQuotationOwnerLabel(quotation, (id) =>
    resolveUserDisplay(id, 'Unknown user')
  );
  const projectLabel = formatQuotationProjectLabel(quotation);
  const isTablet = useMediaQuery('(max-width: 1023px)');
  const [compareOpen, setCompareOpen] = useState(false);
  const [showChangedLinesOnly, setShowChangedLinesOnly] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const role = resolveFamilyRole(quotation);
  const isHistorical = role === 'superseded';
  const revLabel = displayQuotationRevisionLabel(quotation.revision_number);

  const workflowStages = useMemo(() => buildRevisionWorkflowStages(quotation), [quotation]);
  const workflowCurrent = useMemo(() => buildRevisionWorkflowCurrentIndex(quotation), [quotation]);
  const insights = useMemo(
    () => buildQuotationRevisionInsights(quotation, family, { universalApprovalPending }),
    [quotation, family, universalApprovalPending]
  );

  const hasActiveCr = contractReviews.some(
    (r) => r.review_status !== 'Rejected' && r.review_status !== 'Cancelled' && !r.is_stale
  );
  const hasApprovedCr = contractReviews.some(
    (r) =>
      r.review_status === 'Approved' &&
      !r.is_stale &&
      (r.final_recommendation !== 'approved_with_conditions' || !!r.conditions_acknowledged_at)
  );

  const primaryCta = resolveQuotationPrimaryCta({
    quotation,
    universalApprovalPending,
    universalApprovalApproved,
    hasActiveContractReview: hasActiveCr,
    hasApprovedContractReview: hasApprovedCr,
    isTradingRental,
    currentOfferId: currentOffer?.id,
  });

  const statusColorMap: Record<string, string> = {
    Draft: 'default',
    Sent: 'processing',
    Accepted: 'success',
    Rejected: 'error',
    Revised: 'warning',
  };

  const roleBadge =
    role === 'current_offer' ? (
      <Tag color="blue">Current Customer Offer</Tag>
    ) : role === 'accepted' ? (
      <Tag color="green">Customer Accepted</Tag>
    ) : role === 'superseded' ? (
      <Tag>{revLabel} — SUPERSEDED</Tag>
    ) : (
      <Tag color="gold">{revLabel}</Tag>
    );

  const moreActions: MenuProps['items'] = [
    { key: 'compare', label: 'Compare Revisions' },
    { key: 'print', label: `Print ${revLabel}`, icon: <PrinterOutlined /> },
    canRevise && !isHistorical ? { key: 'revise', label: 'Create Revision' } : null,
    canEdit && !isHistorical ? { key: 'edit', label: 'Edit', icon: <EditOutlined /> } : null,
    { key: 'transfer', label: 'Transfer Team', icon: <SwapOutlined /> },
    onOpenEstimate ? { key: 'estimate', label: 'View Estimate' } : null,
    contractReviews[0]
      ? { key: 'cr', label: 'View Contract Review' }
      : null,
    quotation.sales_order_id
      ? { key: 'so', label: 'View Customer Order' }
      : null,
  ].filter(Boolean) as MenuProps['items'];

  const handleMore = ({ key }: { key: string }) => {
    if (key === 'compare') setCompareOpen(true);
    if (key === 'print') actions.onPrint();
    if (key === 'revise') actions.onRevise();
    if (key === 'edit') actions.onEdit?.();
    if (key === 'transfer') actions.onTransfer();
    if (key === 'estimate') onOpenEstimate?.();
    if (key === 'cr' && contractReviews[0]) navigate(`/growth/contract-reviews/${contractReviews[0].id}`);
    if (key === 'so' && quotation.sales_order_id) navigate(`/growth/orders/${quotation.sales_order_id}`);
  };

  const handleInsightAction = (key: NonNullable<(typeof insights)[0]['actionKey']>) => {
    if (key === 'open_current' && currentOffer) {
      navigate(`/growth/quotations/${currentOffer.id}`);
      return;
    }
    if (key === 'revise') actions.onRevise();
    if (key === 'send' || key === 'accept' || key === 'convert' || key === 'submit_approval') {
      actions.onPrimaryCta(
        key === 'send'
          ? 'send'
          : key === 'accept'
            ? 'accept'
            : key === 'convert'
              ? 'convert_so'
              : 'submit_approval'
      );
    }
  };

  const filteredItems = useMemo(() => {
    const items = (quotation.items || []).sort((a, b) => a.line_number - b.line_number);
    if (!showChangedLinesOnly || !comparison?.line_deltas?.length) return items;
    const changedKeys = new Set(comparison.line_deltas.map((d) => d.key));
    return items.filter(
      (it) =>
        changedKeys.has(String(it.line_number)) ||
        (it.item_code && changedKeys.has(it.item_code))
    );
  }, [quotation.items, showChangedLinesOnly, comparison]);

  const profitabilityMarkupPct = (() => {
    const snap = quotation.profitability_snapshot;
    if (!canViewProfitability || !snap) return null;
    const cost = Number(snap.loaded_total_cost);
    const profit = Number(snap.gross_profit);
    if (!(cost > 0) || !Number.isFinite(profit)) return null;
    return (profit / cost) * 100;
  })();

  const tabItems = [
    {
      key: 'overview',
      label: 'Overview',
      children: (
        <Card size="small" title="Overview">
          <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
            <Descriptions.Item label="Customer">{quotation.customer_name}</Descriptions.Item>
            <Descriptions.Item label="Project">{projectLabel}</Descriptions.Item>
            <Descriptions.Item label="Department">
              {departmentLabel(quotation.department_id, departments)}
            </Descriptions.Item>
            <Descriptions.Item label="Opportunity">
              {quotation.opportunity_summary?.opportunity_number || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Estimate">{estimateLabel || '—'}</Descriptions.Item>
            <Descriptions.Item label="Owner">{ownerLabel}</Descriptions.Item>
            <Descriptions.Item label="Quotation Date">
              {dayjs(quotation.quotation_date).format('DD MMM YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label="Valid Until">
              {quotation.valid_until ? dayjs(quotation.valid_until).format('DD MMM YYYY') : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={statusColorMap[quotation.status]}>{quotation.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Approval">{quotation.approval_status}</Descriptions.Item>
            <Descriptions.Item label="Current Offer">
              {quotation.is_current_submitted ? 'Yes' : 'No'}
            </Descriptions.Item>
            <Descriptions.Item label="Subject" span={2}>
              {quotation.quotation_name}
            </Descriptions.Item>
          </Descriptions>
          {overviewExtra}
        </Card>
      ),
    },
    {
      key: 'items',
      label: 'Items',
      children: (
        <Card
          size="small"
          title="Line Items"
          extra={
            comparison?.line_deltas?.length ? (
              <Space>
                <Text type="secondary">Show changed only</Text>
                <Switch checked={showChangedLinesOnly} onChange={setShowChangedLinesOnly} />
              </Space>
            ) : null
          }
        >
          <Table
            columns={[
              ...itemColumns.slice(0, 1),
              {
                title: '',
                key: 'chg',
                width: 120,
                render: (_: unknown, record: QuotationItem) => (
                  <QuotationItemsChangedBadge
                    lineNumber={record.line_number}
                    itemCode={record.item_code}
                    comparison={comparison}
                  />
                ),
              },
              ...itemColumns.slice(1),
            ]}
            dataSource={filteredItems}
            rowKey="id"
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        </Card>
      ),
    },
    {
      key: 'commercial',
      label: 'Commercial',
      children: (
        <Card size="small" title="Commercial Summary">
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Gross Sell">
              {quotation.currency} {Number(quotation.subtotal).toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label="Discount">
              {quotation.discount_percentage}% ({quotation.currency}{' '}
              {Number(quotation.discount_amount).toFixed(2)})
            </Descriptions.Item>
            <Descriptions.Item label="Net Sell">
              {quotation.currency} {Number(quotation.total_after_discount).toFixed(2)}
            </Descriptions.Item>
            {canViewProfitability && quotation.profitability_snapshot ? (
              <>
                <Descriptions.Item label="Estimated Cost">
                  {Number(quotation.profitability_snapshot.loaded_total_cost).toFixed(2)}
                </Descriptions.Item>
                <Descriptions.Item label="Margin">
                  {Number(quotation.profitability_snapshot.gross_profit).toFixed(2)} (
                  {Number(quotation.profitability_snapshot.gross_profit_percentage).toFixed(2)}%)
                </Descriptions.Item>
                <Descriptions.Item label="Markup %">
                  {profitabilityMarkupPct != null ? `${profitabilityMarkupPct.toFixed(2)}%` : '—'}
                </Descriptions.Item>
              </>
            ) : null}
            <Descriptions.Item label="VAT">
              {quotation.currency} {Number(quotation.vat_amount).toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label="Grand Total">
              <Text strong>{quotation.currency} {Number(quotation.grand_total).toFixed(2)}</Text>
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ),
    },
    {
      key: 'terms',
      label: 'Terms',
      children: (
        <Card size="small" title="Commercial Terms">
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Payment">{quotation.payment_terms || '—'}</Descriptions.Item>
            <Descriptions.Item label="Advance">
              {quotation.advance_payment_percentage != null
                ? `${quotation.advance_payment_percentage}%`
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Delivery">
              {quotation.delivery_terms ? (
                <div
                  className="qtn-rich-text"
                  dangerouslySetInnerHTML={{ __html: richTextToPrintHtml(quotation.delivery_terms) }}
                />
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Validity">{quotation.validity_days} days</Descriptions.Item>
            <Descriptions.Item label="Notes">
              {quotation.notes ? (
                <div
                  className="qtn-rich-text"
                  dangerouslySetInnerHTML={{ __html: richTextToPrintHtml(quotation.notes) }}
                />
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Terms & Conditions">
              {quotation.terms_and_conditions ? (
                <div
                  className="qtn-rich-text"
                  dangerouslySetInnerHTML={{ __html: richTextToPrintHtml(quotation.terms_and_conditions) }}
                />
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Special Instructions">
              {quotation.special_instructions ? (
                <div
                  className="qtn-rich-text"
                  dangerouslySetInnerHTML={{ __html: richTextToPrintHtml(quotation.special_instructions) }}
                />
              ) : (
                '—'
              )}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ),
    },
    {
      key: 'revisions',
      label: 'Revisions',
      children: (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <QuotationRevisionHistoryTab
            family={family}
            onCompare={() => setCompareOpen(true)}
          />
          <Card size="small" title="Timeline">
            <QuotationRevisionTimeline quotation={quotation} family={family} />
          </Card>
        </Space>
      ),
    },
    {
      key: 'approval',
      label: 'Approval',
      children: (
        <>
          <QuotationApprovalImpactCard comparison={comparison} />
          {showUniversalApproval && onApproveViaDocument && onRejectViaDocument ? (
            <ApprovalPanel
              entityType={ENTITY_TYPE_QUOTATION}
              entityId={quotation.id}
              onApproveViaDocument={onApproveViaDocument}
              onRejectViaDocument={onRejectViaDocument}
              extraInvalidateQueryKeys={[]}
            />
          ) : (
            <Text type="secondary">No active approval instance for this revision.</Text>
          )}
        </>
      ),
    },
    {
      key: 'submission',
      label: 'Submission',
      children: <QuotationSubmissionHistoryPanel quotationId={quotation.id} embedded />,
    },
    {
      key: 'documents',
      label: 'Documents',
      children: (
        <Card size="small" title="Documents">
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {!isHistorical ? (
              <DocumentUpload entityType="Quotation" entityId={quotation.id} onSuccess={() => {}} />
            ) : null}
            <DocumentList entityType="Quotation" entityId={quotation.id} />
          </Space>
          <InheritedAttachmentsCard trailDomain="sales" entityType="Quotation" entityId={quotation.id} />
        </Card>
      ),
    },
    {
      key: 'activity',
      label: 'Activity',
      children: <QuotationActivityTab quotation={quotation} family={family} />,
    },
  ];

  return (
    <>
      <WorkspaceLayout
        fullBleed
        header={
          <EnterprisePageHeader
            documentLabel="Sales Quotation"
            documentNumber={formatQuotationPrintIdentity(
              quotation.quotation_number,
              quotation.revision_number
            )}
            statusTag={
              <Space wrap>
                <Tag color={statusColorMap[quotation.status]}>{quotation.status}</Tag>
                {roleBadge}
                {approvalSummary?.instance?.status === 'pending_approval' ? (
                  <PendingApproverText
                    compact
                    prefix="Next approver"
                    pendingApproverLabel={approvalSummary?.pendingApproverLabel}
                    pendingApprovers={approvalSummary?.pendingApprovers}
                  />
                ) : null}
              </Space>
            }
            subtitle={
              <Space wrap size={[12, 4]}>
                <Text>Customer: {quotation.customer_name}</Text>
                {projectLabel !== '—' ? <Text>Project: {projectLabel}</Text> : null}
                {estimateLabel ? <Text>Estimate: {estimateLabel}</Text> : null}
                <Text>Owner: {ownerLabel}</Text>
                {quotation.valid_until ? (
                  <Text>Valid Until: {dayjs(quotation.valid_until).format('DD MMM YYYY')}</Text>
                ) : null}
              </Space>
            }
            facts={
              <QuotationRevisionSelector
                quotation={quotation}
                family={family}
                loading={familyLoading}
                onSelect={(nextId) => navigate(`/growth/quotations/${nextId}`)}
              />
            }
            primaryActions={
              <Space wrap>
                <Button icon={<ArrowLeftOutlined />} onClick={actions.onBack}>
                  Back
                </Button>
                {!isHistorical ? (
                  <QuotationPrimaryCtaButton
                    cta={primaryCta}
                    loading={actions.primaryCtaLoading}
                    onAction={actions.onPrimaryCta}
                  />
                ) : (
                  <QuotationPrimaryCtaButton
                    cta={{ key: 'open_current', label: 'Open Current Revision' }}
                    onAction={actions.onPrimaryCta}
                  />
                )}
                <ReportDocumentActions
                  reportKey="sales-quotation"
                  params={{ id: quotation.id }}
                  documentId={quotation.id}
                  sendPermissions={['crm.quotation.send', 'growth.view']}
                  emailLabel="Email customer"
                />
                {isTablet ? (
                  <QuotationIntelligenceRail
                    mode="drawer"
                    quotation={quotation}
                    estimateLabel={estimateLabel}
                    estimationRevision={currentEstimationRevision}
                    onOpenEstimate={onOpenEstimate}
                    contractReviews={contractReviews}
                    salesOrderId={quotation.sales_order_id}
                    salesOrderNumber={salesOrderNumber}
                    linkedSalesOrders={linkedSalesOrders}
                    onOpenContractReview={(crId) => navigate(`/growth/contract-reviews/${crId}`)}
                    onOpenSalesOrder={() =>
                      quotation.sales_order_id && navigate(`/growth/orders/${quotation.sales_order_id}`)
                    }
                    onOpenLinkedSalesOrder={(soId) => navigate(`/growth/orders/${soId}`)}
                    showBrief={!!quotation.approval_instance_id}
                  />
                ) : null}
              </Space>
            }
            moreActions={moreActions}
            onMoreClick={handleMore}
          />
        }
        workflow={<WorkflowStrip stages={workflowStages} current={workflowCurrent} compact />}
        rightRail={
          !isTablet ? (
            <QuotationIntelligenceRail
              quotation={quotation}
              estimateLabel={estimateLabel}
              estimationRevision={currentEstimationRevision}
              onOpenEstimate={onOpenEstimate}
              contractReviews={contractReviews}
              salesOrderId={quotation.sales_order_id}
              salesOrderNumber={salesOrderNumber}
              linkedSalesOrders={linkedSalesOrders}
              onOpenContractReview={(crId) => navigate(`/growth/contract-reviews/${crId}`)}
              onOpenSalesOrder={() =>
                quotation.sales_order_id && navigate(`/growth/orders/${quotation.sales_order_id}`)
              }
              onOpenLinkedSalesOrder={(soId) => navigate(`/growth/orders/${soId}`)}
              showBrief={!!quotation.approval_instance_id}
            />
          ) : undefined
        }
      >
        <QuotationHistoricalBanner
          quotation={quotation}
          currentOffer={currentOffer}
          onOpenCurrent={
            currentOffer ? () => navigate(`/growth/quotations/${currentOffer.id}`) : undefined
          }
        />
        <QuotationAcceptanceBanner
          quotation={quotation}
          isTradingRental={isTradingRental}
          canProceed={!isHistorical}
          onProceedToContractReview={() => actions.onPrimaryCta('create_contract_review')}
          onConvertToSalesOrder={() => actions.onPrimaryCta('convert_so')}
        />
        {quotation.status === 'Draft' &&
        latestApprovedEstimateId &&
        latestApprovedEstimateRevision != null &&
        currentEstimationRevision != null &&
        latestApprovedEstimateRevision > currentEstimationRevision ? (
          <div style={{ marginBottom: 12 }}>
            <QuotationEstimateChangeBanner
              estimateLabel={`${estimateLabel ?? 'Estimate'} REV ${latestApprovedEstimateRevision}`}
              costDelta={estimateCostDelta}
              onReview={() => onOpenEstimateReconciliation?.(latestApprovedEstimateId)}
            />
          </div>
        ) : null}
        <QuotationCommercialHero quotation={quotation} compact={isTablet} />
        <QuotationRevisionImpactHero comparison={comparison} loading={compareLoading} />
        <QuotationAttentionPanel
          quotation={quotation}
          insights={insights}
          comparison={comparison}
          universalApprovalPending={universalApprovalPending}
          onInsightAction={handleInsightAction}
        />
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </WorkspaceLayout>

      <QuotationCompareDrawer open={compareOpen} quotation={quotation} onClose={() => setCompareOpen(false)} />
      <QuotationSendConfirmModal
        open={!!sendConfirmOpen}
        quotation={quotation}
        priorOfferRevision={priorOfferRevision}
        loading={sendLoading}
        onCancel={() => onSendCancel?.()}
        onConfirm={() => onSendConfirm?.()}
      />
      <QuotationAcceptConfirmModal
        open={!!acceptConfirmOpen}
        quotation={quotation}
        estimateLabel={estimateLabel}
        loading={acceptLoading}
        onCancel={() => onAcceptCancel?.()}
        onConfirm={() => onAcceptConfirm?.()}
      />
    </>
  );
};

export default QuotationNegotiationWorkspace;
